<article class="">
    <header class="">
        <div class="">
            <ul class="list-inline d-flex justify-content-between mb-0">
                <li class="list-inline-item">
                    <h1 class="">Future Rates</h1>
                </li>
                <li class="list-inline-item">
                    <button class="btn btn-icon btn-light-info mx-1" (click)="printPage()">
                        <i class="ti ti-printer"></i>
                    </button>
                    <button aria-label="Copy link" class="btn btn-icon btn-light-info" (click)="copyFullPageText()">
                        <i class="ti ti-copy"></i>
                    </button>
                </li>
            </ul>
        </div>
    </header>
    <div class="">
        <div class="card-body p-2">
            <div>
                <p class="mt-1">Future rates are rates found in our database that are associated with contracts that
                    begin in the
                    future or are otherwise valid beyond the current day.</p>
                <p>The future period for rates under short-term contracts is limited by 10 days, while long-term rates
                    can be displayed up to 3 months into the future.</p>
            </div>
            <blockquote class="my-4 p-3 bg-yellow-100 color-block">For a predictive view of possible long-term rates
                over a longer time
                period, see Market rate outlook.</blockquote>
            <h2>Overview</h2>
            <p>Xeneta provides market trends and benchmarks based on current and historical freight rates, but there are
                always new freight contracts being signed by market players.</p>
            <p>In some cases, contracts negotiated near the present day may be scheduled to start a few weeks or months
                into the future, but they are still being signed today and reflect today's market.</p>
            <p>In other cases, long-term contracts that extend past the present day also reflect the near-present market
                as they tend to be stable compared to short-term contract rates.</p>
            <p>Using this future rate period, you can extend your view of the market on <a
                    data-elevio-article="49"><span aria-label="Benchmarks Graph"
                        data-elevio-article="49">Benchmarks</span></a> and <a data-elevio-article="48"><span
                        aria-label="Trends Graph" data-elevio-article="48">Trends</span></a>
                graphs to see both long-term and short-term rates that are valid up to 3 months and 10 days into the
                future, respectively.</p>
            <h2>Understanding Future Rates</h2>
            <p>If there is a contracted rate that is valid for one year and began three months ago, it will be
                considered part of the future period because it extends nine months into the future.</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/3t7eulYf3EGBEonYPCYE2pTTXqU1L0UVdr5VUoclsjM/future-rate-period-01-0u0.png"
                    class="fr-fic fr-dib"></p>
            <p>Similarly, if there is a recently signed contract that is set to take effect within a month, then its
                associated rate would too be part of the future period, since it extends beyond the current day.</p>
            <p>Any rates displayed within the future period will eventually become part of regular historical data.
                Think of it as a window into the very first contracts currently being signed for the near future.</p>
            <h2>Viewing Future Rates</h2>
            <p>To view future rates in Xeneta:</p>
            <ul>
                <li>Go to the <strong>Benchmark</strong><strong>s</strong> or <strong>Trends</strong> graph for your
                    selected origin-destination pair</li>
                <li>Click on the end date part of the date range selector, and then click <strong>Show
                        Futures&nbsp;</strong>in the opened dialog</li>
            </ul>
            <p><img src="../../../../../assets/img/date_filter.png" class="fr-fic fr-dii"></p>
            <p>If you selected long-term rates, the graph will be updated to show an additional three-month period into
                the future, indicated by a gray background:</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/OsXUK8CMmUx1vDr8PwBSWM9sRPWO6WmBP5woeTjr850/benchmarks-future-rates-0Tw.png"
                    class="fr-fic fr-dii"></p>
            <p>For short-term rates, this additional view will cover a 10-day period into the future:</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/qrtdBRkiPgDUSiTfeqcmJVGvxp7ryHxm2iqAflaWU54/future-rates-short-01-0dw.png"
                    class="fr-fic fr-dib"></p>
            <h3>Future Rates and Contract Age&nbsp;</h3>
            <p>The future rates you see on the graphs will depend on the selected
                <span class="text-primary cursor-pointer" (click)="goToContractedWithin('Contracted Within')">Contracted
                    Within</span> option that limits the age of
                the aggregated contracts.
            </p>
            <p>In the example we'll cover below, we’ll explore a four-month period where three of those months are part
                of the future window, and take a look at the <strong>Shenzhen to</strong><strong>&nbsp;Hamburg</strong>
                trade lane using three different <strong>Contracted Within</strong> filter settings for long-term
                contracts:</p>
            <ul>
                <li><strong>All Contracts</strong></li>
                <li><strong>Contracted Within Last 6 months</strong></li>
                <li><strong>Contracted Within Last 3 months</strong></li>
            </ul>
            <h4>All Contracts</h4>
            <p>By including all long-term contracts, we can see that the rates will continue their downward trend for
                contracts running beyond the current day.</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/1CLjfI7Q59ig256LeHafCd9_G9LrJxKOwS_UHwGEPoc/future-rates-graph-02-WBo.png"
                    class="fr-fic fr-dii"></p>
            <blockquote class="my-4 p-3 bg-gray-300 color-block">
                <p class="m-0">This view does not reveal very much because older long-term contracts tend to outnumber
                    newer
                    freshly-signed contracts.</p>
            </blockquote>
            <h4>Contracted Within Last 6 Months</h4>
            <p>If we apply the Contracted Within filter to exclude all contracts older than 6 months, we get the
                following view of the market:</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/rD58ciE8mbGY8ofVZKL9qG_PyPvBXwkoLy6Jm4HBq-w/future-rates-graph-03-Vt0.png"
                    class="fr-fic fr-dii"></p>
            <p>We see that the average market rate will see quite a significant dip in the beginning of April, but
                should remain around the same level for the months that follow.</p>
            <h4>Contracted Within Last 3 Months</h4>
            <p>Finally, by looking only at contracts that were signed within the last 3 months, we get the "freshest"
                view of the ocean freigh market.</p>
            <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/8CanjTZ2yvyrz74FEkPbUzbmlSNWqVxNSP43QDoWTyw/future-rates-graph-04-Yjk.png"
                    class="fr-fic fr-dii"></p>
            <p>This view also seems to confirm that the rates for the newest contracts on the market will drop more than
                30% in April, and should stay around that level at least until the end of the month.</p>
            <blockquote class="my-4 p-3 bg-yellow-100 color-block">
                <p class="m-0">You’ll notice that the line depicting the market rate disappears around the end of
                    October. This is
                    due to the fact that there simply aren't that many contracts that are at most three months old and
                    that extend into the future for Xeneta to display.</p>
            </blockquote>
            <p>The conclusion we can draw in this particular example is that market players entering contracts around
                the present day seem to be paying lower rates for shipments scheduled for the coming months.</p>
        </div>
    </div>
</article>